import type { Feature } from '../features';

export type UpsellCopy = {
  /** A statement about what the capability does. Never a statement about what you lack. */
  title: string;
  description: string;
};

export type UpsellCopyEntry = UpsellCopy & {
  /**
   * The same sentence with the vendor taken out of it, present ONLY on the entries that name
   * the product.
   *
   * A white-label tenant must never read our name (D6), and this register is rendered
   * verbatim by `UpsellDialog` — title as the dialog's accessible name, and again as the
   * `capability` in the outbound mail subject. Titles below do name Nexoya, so on a white-label
   * tenant the branded string reached both the screen and an email addressed to the agency's
   * own support desk.
   *
   * An explicit rewrite rather than a search-and-replace over the branded string: "Your
   * Nexoya contact" and "the full Nexoya platform" need different repairs, and a regex that
   * gets one of them right mangles the other into "your the platform contact" with no test
   * able to tell. `copy.test.ts` sweeps EVERY feature for `/nexoya/i`, so an entry that gains
   * the product name without gaining a `neutral` fails there.
   */
  neutral?: UpsellCopy;
};

/** The tier word, alone, so every surface that says it derives it from here. */
export const PLAN_TIER_WORD = 'Premium';

/**
 * The product name, in one place.
 *
 * `interestMailto.ts` composes the mail subject from this rather than from its own literal.
 * It carried "Nexoya Full" for a release after this register had been renamed to Premium, and
 * nothing caught it: `copy.test.ts` forbade the old name, `interestMailto.test.ts` pinned it,
 * and no test imported both.
 */
export const PRODUCT_NAME = `Nexoya ${PLAN_TIER_WORD}` as const;

/**
 * The reader's OWN plan, in one place — `PRODUCT_NAME` names the upgrade, this names the plan
 * they are on. It carries the vendor name, so it must never render on a white-label tenant.
 */
export const CURRENT_PLAN_NAME = 'Nexoya Starter' as const;

/**
 * The revenue CTA — one label on every tenant, and in the unresolved state too.
 *
 * Safe there only because it names neither vendor nor tier: the ACTION forks (demo link on the
 * Nexoya tenant, mailto to the tenant's own support desk otherwise), the LABEL does not. A tier
 * word here is what forced that fork open and left two labels for one intent.
 */
export const INTEREST_CTA_LABEL = "I'm interested";

/**
 * The vocabulary, decided once.
 *
 * - The product is **Nexoya Premium**. Not Full, not Pro, not Plus, not Enterprise — the
 *   test beside this file pins the name and rejects the alternates, because a half-applied
 *   rename is how a dialog ends up selling two products. The tier word names the product
 *   only; "Locked" is the state word on badges (`LockedAction`, `PlanTabLabel`), which read
 *   "Locked — available on Nexoya Premium".
 * - Framing is present-tense and positive — "runs on this portfolio's data", not
 *   "not included in your plan". Denial language is the register `<Can>` uses for role
 *   refusals; a commercial lock has to read as an invitation.
 * - No countdowns, no seat counts, no discounts. Procurement-trained buyers read
 *   manufactured urgency as a negotiating tell.
 * - The person reading this is an analyst who cannot buy, so nothing here promises them
 *   a checkout.
 */
export const UPSELL_COPY: Partial<Record<Feature, UpsellCopyEntry>> & Record<string, UpsellCopyEntry | undefined> = {
  // The product owner's own wording, verbatim. `optimization.contentControls` still names the
  // vendor, so it keeps a `neutral` twin; the others name none and need none.
  'optimization.view': {
    title: 'Optimize budget and bid targets across channels.',
    description:
      'Unlock a performance uplift with cross-channel portfolio optimization: more conversions with your budget or the same level of conversions with less budget.',
  },
  'optimization.settings': {
    title: 'Optimization settings shape every budget application.',
    description: 'Set the budget reallocation risk level and the budget delta handling for this portfolio.',
  },
  'optimization.contentControls': {
    title: 'Per-campaign optimization controls.',
    description: 'Choose which campaigns Nexoya may reallocate budget between, and the limits it has to respect.',
    neutral: {
      title: 'Per-campaign optimization controls.',
      description:
        'Choose which campaigns optimization may reallocate budget between, and the limits it has to respect.',
    },
  },
  'budget.reallocation.view': {
    title: 'See where Nexoya would reallocate your budget.',
    description: 'The proposed shifts appear in your Budget tab, before anything is applied.',
    neutral: {
      title: 'See where optimization would reallocate your budget.',
      description: 'The proposed shifts appear in your Budget tab, before anything is applied.',
    },
  },
  'validation.view': {
    title: 'Validation',
    description:
      'The optimization proposals are validated to determine how much the optimization has brought you in return and how accurate the performance predictions were.',
  },
  'simulation.view': {
    title: 'Simulate a budget change before you make it.',
    description:
      'Scenario simulation allows you to predict and simulate different business outcomes depending on various levels of budget increases or decreases.',
  },
  'metrics.view': {
    title: 'Metrics of your own, built from the data you already send.',
    description:
      'Compose custom metrics, chart them over any period and put them beside the channel data they come from.',
  },
  'attribution.view': {
    title: 'Attribution shows which channels actually drove your conversions.',
    description:
      'Privacy-safe, regression-based modeling across every channel in this team — no cookies, no pixels, no individual user signals.',
  },
  'attribution.configure': {
    title: 'Attribution rules decide how credit is assigned.',
    description: 'Configure the model per portfolio and compare attributed impact against measured impact.',
  },
  'channel.unrestricted': {
    title: 'Connect every channel Nexoya supports.',
    description: `${PRODUCT_NAME} opens the full catalog of 40+ channel, analytics and CRM integrations beyond the ones included here.`,
    neutral: {
      title: 'Connect every channel the platform supports.',
      description:
        'The full platform opens the whole catalog of 40+ channel, analytics and CRM integrations beyond the ones included here.',
    },
  },
};

const FALLBACK: UpsellCopyEntry = {
  title: 'This is part of the full Nexoya platform.',
  description: 'Your Nexoya contact can walk you through what it does on the portfolios you manage.',
  neutral: {
    title: 'This is part of the full platform.',
    description: 'Your support team can walk you through what it does on the portfolios you manage.',
  },
};

export function upsellCopyFor(feature: Feature): UpsellCopyEntry {
  return UPSELL_COPY[feature] ?? FALLBACK;
}

/**
 * The same copy with no vendor in it, for a white-label tenant.
 *
 * Falls through to the branded entry when it has no `neutral` — most of them do not need one,
 * because most of this register never named the product in the first place. `copy.test.ts`
 * sweeps every feature, so "needs one and does not have one" is a red test rather than a
 * judgement call.
 */
export function neutralUpsellCopyFor(feature: Feature): UpsellCopy {
  const entry = upsellCopyFor(feature);
  return entry.neutral ?? { title: entry.title, description: entry.description };
}
